import { useEffect, useRef, useState } from 'react'
import { ICellRendererParams } from 'ag-grid-community'

/**
 * ag-grid 컬럼에서 문자열 배열을 셀 너비에 맞게 잘라 "앞 k개 ...외 n건" 형태로 표시하는 cellRenderer.
 * @param getItems 표시할 문자열 배열을 row 데이터에서 추출하는 함수
 * @param getTotalCount 실제 총 개수를 반환하는 함수. BE에서 배열을 일부만 내려줄 때 사용.
 *   없으면 getItems 결과의 length로 대체한다.
 * @param getOverrideText 배열 대신 고정 텍스트로 표시해야 하는 특수 케이스 처리. 해당 없으면 null 반환
 * @param emptyText 배열이 비었을 때 표시할 텍스트 (기본값: '-')
 * @param unit "...외 n건"에서 단위 문자열 (기본값: '건')
 */
export interface TruncatedListCellRendererParams<TData> extends ICellRendererParams<TData> {
  getItems: (data: TData) => string[]
  getTotalCount?: (data: TData) => number
  getOverrideText?: (data: TData) => string | null
  emptyText?: string
  unit?: string
}

export function TruncatedListCellRenderer<TData>({
  data,
  getItems,
  getTotalCount,
  getOverrideText,
  emptyText = '-',
  unit = '건',
}: TruncatedListCellRendererParams<TData>) {
  const ref = useRef<HTMLDivElement>(null)
  const [displayText, setDisplayText] = useState('')

  useEffect(() => {
    const el = ref.current
    if (!el || !data) return

    const override = getOverrideText?.(data)
    if (override != null) {
      setDisplayText(override)
      return
    }

    const items = getItems(data)
    if (items.length === 0) {
      setDisplayText(emptyText)
      return
    }

    const totalCount = getTotalCount ? getTotalCount(data) : items.length

    const containerWidth = el.offsetWidth
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      setDisplayText(items.join(', '))
      return
    }
    ctx.font = getComputedStyle(el).font

    let fittingCount = 0
    for (let i = 0; i < items.length; i++) {
      const remaining = totalCount - (i + 1)
      const text = items.slice(0, i + 1).join(', ') + (remaining > 0 ? ` ...외 ${remaining}${unit}` : '')
      if (ctx.measureText(text).width <= containerWidth) {
        fittingCount = i + 1
      } else {
        break
      }
    }

    if (fittingCount >= items.length) {
      const extraCount = totalCount - items.length
      setDisplayText(extraCount > 0 ? `${items.join(', ')} ...외 ${extraCount}${unit}` : items.join(', '))
    } else if (fittingCount === 0) {
      if (totalCount === 1) {
        setDisplayText(items[0])
      } else {
        const suffix = ` 외 ${totalCount - 1}${unit}`
        const ellipsis = '...'
        const availableWidth = containerWidth - ctx.measureText(suffix).width
        let truncated = ''
        for (let j = 0; j < items[0].length; j++) {
          if (ctx.measureText(items[0].slice(0, j + 1) + ellipsis).width <= availableWidth) {
            truncated = items[0].slice(0, j + 1)
          } else {
            break
          }
        }
        setDisplayText(truncated ? `${truncated}${ellipsis}${suffix}` : items[0])
      }
    } else {
      setDisplayText(`${items.slice(0, fittingCount).join(', ')} ...외 ${totalCount - fittingCount}${unit}`)
    }
  }, [data])

  return (
    <div ref={ref} style={{ overflow: 'hidden', whiteSpace: 'nowrap', width: '100%', textOverflow: 'ellipsis' }}>
      {displayText}
    </div>
  )
}
